import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";

import { BettingState, RUNNER_FAILURE_CODES, VALIDATION_TYPES } from "@ppb/betslip-core";
import {
  BetslipSportsbookIncrementStakeAction,
  BetslipSportsbookLoginToPlaceBetClickAction,
  BetslipSportsbookPlaceBetsClick,
  BetslipSportsbookRemoveLegClick,
  BetslipSportsbookRemoveSelectionsClick,
  BetslipSportsbookTabSwitchAction,
  UI__BETSLIP_SBK_DEPOSIT_TO_PLACE_BET_CLICK,
  UI__BETSLIP_SBK_INCREMENT_STAKE_ACTION,
  UI__BETSLIP_SBK_LOGIN_TO_PLACE_BET_CLICK,
  UI__BETSLIP_SBK_PLACE_BETS_CLICK,
  UI__BETSLIP_SBK_REMOVE_LEG_CLICK,
  UI__BETSLIP_SBK_REMOVE_SELECTIONS,
  UI__BETSLIP_SBK_TAB_SWITCH,
} from "@ppb/tbd-store/actions/betslip";
import { BetslipSportsbookDepositToPlaceBetClick } from "../../store/state/actions";
import {
  BETTING__DEPOSIT_TO_PLACE_BET,
  BETTING__SBK_BONUS_TOGGLE_ACTION,
  BETTING__SBK_CLEAR_ACTION,
  BETTING__SBK_CONFIRM_BETS,
  BETTING__SBK_INCREMENT_STAKE_ACTION,
  BETTING__SBK_PLACE_BETS,
  BETTING__SBK_REMOVE_LEG_ACTION,
  BETTING__SPORTSBOOK_REMOVE_ALL_FREE_BETS_WALLETS_ACTION,
  BettingDepositToPlaceBetAction,
  BettingSportsbookBonusToggleAction,
  BettingSportsbookClearAction,
  BettingSportsbookConfirmBetsAction,
  BettingSportsbookIncrementStakeAction,
  BettingSportsbookPlaceBetsAction,
  BettingSportsbookRemoveAllWalletsAction,
  BettingSportsbookRemoveLegAction,
} from "@ppb/tbd-store/actions/betting";
import {
  hasAllMarketClosedFailure,
  hasAllMarketSuspendedFailure,
  hasAnyMarketClosedFailure,
  hasAnyMarketSuspendedFailure,
  isBetBuilder,
  isCast,
  isLotteries,
  isMultiBetBuilder,
} from "@ppb/tbd-store/helpers/sportsbook-betting";
import { createOddsMovementSelector } from "../../store/state/betslip-module/betslip-odds-movement-selectors";
import { getSportsbookBettingState } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import {
  createCombinationGroupFailuresSelector,
  createGetBetBuilderCombinationIdsSelector,
  createGetBoostedCombinationsSelector,
  createGetBoostedUncombinedGroupIdsSelector,
  createGetCastGroupIdsSelector,
  createGetCombinationsSelectedWalletsAmounts,
  createHasMultiplesSelector,
  getSingleCombinationIds,
  getSportsbookBettingImplyRunnerFailures,
  getSportsbookBettingRunners,
  getAllUniqueRunnersFailures,
  createSinglesOnTopSelector,
  createQuickBetslipBetPickerSelector,
} from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import {
  emitAcceptOddsMovementPreferenceToggled,
  emitBetslipDepositToPlaceRequested,
  emitBetslipLoginRequested,
} from "../../contract/betslip-api";
import {
  type BetslipSportsbookAcceptOddsMovementToggleAction,
  UI__BETSLIP_SBK_ACCEPT_ODDS_MOVEMENT_TOGGLE,
} from "../../store/state/preferences/preferences-actions";
import { AlertType, QuickStakesProps, KeyboardSeparator, SwitchOnChange } from "@ppb/the-wall-common/types";
import { Product } from "@ppb/tbd-store/state/entities";

import { SportsbookPlacePanelViewModel } from "./snowflakes/SportsbookPlacePanel/BetslipView/SportsbookPlacePanel.types";
import {
  getBetslipPlaceStatus,
  getHasGenerosityTokens,
  getHasGenerosityWallets,
  getHasUserChangedOddsMovementPreference,
  getSportsbookOddsMovement,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";
import {
  MarketSelection,
  SportsbookObbPlacePanelI18N,
} from "./snowflakes/SportsbookPlacePanel/SportsbookObbPlacePanel.types";
import { BetslipSection } from "../Betslip.types";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import { i18n } from "../../helpers/i18n";
import { buildCombinationOdds, buildPotentialReturns, buildTotalOriginalReturns } from "../betslip-formatters";
import { buildSportsbookTransactionalError } from "../connected-sportsbook-betslip-mapper";
import { createValidationsSelector } from "../Notifier/notifier-mapper";
import {
  buildQuickStakesSelector,
  createGetSbkIsDepositRequiredSelector,
  createGetSbkRequiredDepositValueSelector,
} from "../betslip-mapper";

import { createGetMultipleCombinations } from "./sportsbook-place-mapper";
import { buildFreeBetsAlertMessage, sumWalletsAmounts } from "../../helpers/generosity-wallets";
import { buildSbkPlaceBetButtonLabels } from "../../helpers/betslip-place-button-helper";
import { buildSbkBalanceAfterBetLabel } from "../../helpers/betslip-balance-helper";
import type {
  BetslipLocalization,
  BetslipSession,
  SportsbookBetslipBalance,
  SportsbookBetslipCapabilities,
  SportsbookBetslipConfig,
  SportsbookBetslipPreferences,
} from "../../contract";
import { toKeyboardSeparator } from "../../helpers/keyboard-helper";
import { createGetBettingEventGroupedLegs } from "../MultiBetBuilderCard/multi-bet-builder-card-mapper";
import { getUnavailableBetBuilderCombinationGroups } from "../../helpers/combinations";

export type ContainerProps = {
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  session: BetslipSession;
  capabilities: SportsbookBetslipCapabilities;
  preferences: SportsbookBetslipPreferences;
  balance: SportsbookBetslipBalance;
};

type CardProps = {
  betBuilderIds: string[];
  multiBetBuilderIds: string[];
  boostedCombinationIds: string[];
  failedCombinationGroups: number[];
  failedCombinationGroupIds: string[];
  hasPlaceError: SportsbookPlacePanelViewModel["hasPlaceError"];
  i18n: SportsbookObbPlacePanelI18N;
  isPanelDisabled?: SportsbookPlacePanelViewModel["isPanelDisabled"];
  isPlaceDisabled: SportsbookPlacePanelViewModel["isPlaceDisabled"];
  isSummaryDisabled?: SportsbookPlacePanelViewModel["isSummaryDisabled"];
  totalReturns: SportsbookPlacePanelViewModel["totalReturns"];
  totalOriginalReturns: SportsbookPlacePanelViewModel["totalOriginalReturns"];
  hasSingles: boolean;
  hasOnlyOneSingle: boolean;
  oneLineMultipleId: string;
  isQuickBetslipActive: boolean;
  hasOneLineMultiple: boolean;
  hasMultipleLinesMultiples: boolean;
  hasMultiBetBuilder: boolean;
  hasCastBets: boolean;
  hasPriceBoost: boolean;
  hasBetBuilders: boolean;
  hasLotteries: boolean;
  shouldFocusMultiple?: boolean;
  shouldFocusBetBuilder?: boolean;
  shouldFocusCastBet?: boolean;
  focusedCard?: BetslipSection;
  firstCombinationId?: string;
  isFreeBetsSelected: boolean;
  isEligibleToBonus: boolean;
  isBetConfirmationStepEnabled: boolean;
  isCollapsed?: boolean;
  isOddsMovementOn: boolean;
  showAcceptOddsMovementAlert: boolean;
  oddsMovementLabels: { message: string; detailMessage: string };
  placeBtnLabel: string;
  placeBtnSecondaryLabel?: string | string[];
  placeBtnLoadingLabel?: string;
  reversePlaceBtnLabels?: boolean;
  isLoggedIn: boolean;
  isDepositRequired: boolean;
  requiredDepositValue: number;
  isOddsBoosted: boolean;
  termsUrl?: string;
  shouldDisplayPlaceholder: boolean;
  freeBetsAlertMessage?: string;
  balanceAfterBet?: string;
  currencySymbol: string;
  quickStakes: QuickStakesProps["quickStakes"];
  separator: KeyboardSeparator;
  marketSelections: MarketSelection[];
  isUpsellSuggestionsEnabled?: boolean;
  isSinglesFirst?: boolean;
  withAllPebbleEnabled: boolean;
  multiBetBuilderTitle: string;
  oneLineMultipleTitle: string;
  betBuilderTitles: Record<string, string>;
};
export type StateProps = CardProps | Record<string, never>;

const ACCEPT_ODDS_MOVEMENT_LABELS = {
  message: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT" }),
  detailMessage: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_DESCRIPTION" }),
};

const ODDS_MOVEMENT_ON_LABELS = {
  message: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_ON" }),
  detailMessage: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_ON_DESCRIPTION" }),
};

const ODDS_MOVEMENT_OFF_LABELS = {
  message: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_OFF" }),
  detailMessage: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_OFF_DESCRIPTION" }),
};

const acceptOddsMovementLabels = (oddsMovementEnabled: boolean, hasUserChangedOddsMovement: boolean) => {
  if (!hasUserChangedOddsMovement && !oddsMovementEnabled) {
    return ACCEPT_ODDS_MOVEMENT_LABELS;
  }

  if (oddsMovementEnabled) {
    return ODDS_MOVEMENT_ON_LABELS;
  }

  return ODDS_MOVEMENT_OFF_LABELS;
};

const hasLegsWithMarketFailures = (
  combination: BettingState.Combination,
  bettingLegs: BettingState.LegsMap,
  runnerFailures: BettingState.ImplyRunnerFailuresMap,
) =>
  combination.legs.some((leg) => {
    const bettingLeg = bettingLegs[leg];
    return bettingLeg.runners.some(
      (runner) =>
        runnerFailures[runner] &&
        (hasAnyMarketClosedFailure(runnerFailures[runner]) || hasAnyMarketSuspendedFailure(runnerFailures[runner])),
    );
  });

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const i18nLabels = {
    totalReturns: i18n({ key: "I18N.BETSLIP.TOTAL_RETURNS" }),
    multiples: i18n({ key: "I18N.BETSLIP.MULTIPLES" }),
    additionalMultiples: i18n({ key: "I18N.BETSLIP.ADDITIONAL_MULTIPLES" }),
    singles: i18n({ key: "I18N.BETSLIP.SINGLES" }),
    casts: i18n({ key: "I18N.BETSLIP.CASTS" }),
    removeLabel: i18n({ key: "I18N.BETSLIP.REMOVE_ALL" }),
    oddsLabel: i18n({ key: "I18N.BETSLIP.ODDS" }),
    stakeLabel: i18n({ key: "I18N.BETSLIP.STAKE" }),
    freeBetsLabel: i18n({ key: "I18N.BETSLIP.USE_FREE_BET_WITHOUT_AMOUNT" }),
    oddsMovementUp: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_INCREASE" }),
    oddsMovementDown: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_DECREASE" }),
    eachWay: i18n({ key: "I18N.BETSLIP.EACHWAY" }),
    eachWaySubtitle: i18n({ key: "I18N.BETSLIP.EACHWAY_ODDS_PLACES" }),
    betBuilder: i18n({ key: "I18N.BETSLIP.BET_BUILDER" }),
    multiBetBuilder: i18n({ key: "I18N.BETSLIP.BET_BUILDER_MULTIS" }),
    termsLabel: i18n({ key: "I18N.BETSLIP.BETS_ACCEPTED_IN_ACCORDANCE_WITH" }),
    termsLinkLabel: i18n({ key: "I18N.BETSLIP.TEXT_RULES" }),
    freeBetsAlertRemoveLabel: i18n({ key: "I18N.BETSLIP.REMOVE" }),
    priceBoosts: i18n({ key: "I18N.BETSLIP.GROUPS.PRICE_BOOST" }),
    balanceAfterBet: i18n({ key: "I18N.BETSLIP.BALANCE_AFTER_BET" }),
    voidBlurbText: i18n({ key: "I18N.BETSLIP.OBB.VOID_BLURB" }),
    tabAllTitle: i18n({ key: "I18N.BETSLIP.TABS.ALL" }),
    tabBetBuildersTitle: i18n({ key: "I18N.BETSLIP.TABS.BET_BUILDER" }),
    tabMultiplesTitle: i18n({ key: "I18N.BETSLIP.TABS.MULTIPLES" }),
    tabCastTitle: i18n({ key: "I18N.BETSLIP.TABS.CAST_BET" }),
    tabSinglesTitle: i18n({ key: "I18N.BETSLIP.TABS.SINGLES" }),
    tabLotteriesTitle: i18n({ key: "I18N.BETSLIP.TABS.LOTTERIES" }),
    betslipAriaTitle: i18n({ key: "I18N.BETSLIP.TITLE" }),
    betslipNotAvailable: i18n({ key: "I18N.BETSLIP.NOT_AVAILABLE" }),
  };
  const getBetBuilderCombinationIds = createGetBetBuilderCombinationIdsSelector();
  const getBoostedCombinations = createGetBoostedCombinationsSelector();
  const getFailedCombinationGroups = createCombinationGroupFailuresSelector();
  const getBoostedUncombinedGroupIds = createGetBoostedUncombinedGroupIdsSelector();
  const getMultipleCombinations = createGetMultipleCombinations();
  const getOddsMovementState = createOddsMovementSelector();
  const getValidations = createValidationsSelector();
  const getIsDepositRequired = createGetSbkIsDepositRequiredSelector();
  const getSbkRequiredDepositValueSelector = createGetSbkRequiredDepositValueSelector();
  const getCombinationsSelectedWalletsAmounts = createGetCombinationsSelectedWalletsAmounts();
  const getQuickStakes = buildQuickStakesSelector();
  const getHasMultiples = createHasMultiplesSelector();
  const getCastGroupIds = createGetCastGroupIdsSelector();
  const getSinglesOnTopSelector = createSinglesOnTopSelector();
  const getBettingEventGroupedLegs = createGetBettingEventGroupedLegs();
  const getQuickBetslipBet = createQuickBetslipBetPickerSelector();

  return (
    state: BetslipApplicationState,
    { localization, config, session, capabilities, preferences, balance }: ContainerProps,
  ): StateProps => {
    const { currencySymbol } = localization;
    if (!getIsBetslipLoaded(state)) {
      return {};
    }

    const termsUrl = config.termsUrls.general;

    const isCollapsed = state.betslipModule?.isCollapsed ?? false;
    const hasGenerosityWallets = getHasGenerosityWallets(state);
    const hasGenerosityTokens = getHasGenerosityTokens(state);
    const sportsbookOddsMovement = getSportsbookOddsMovement(state);

    const oddsMovementMap = getOddsMovementState(sportsbookOddsMovement);
    const { oneLineCombination, multiLinesCombinations } = getMultipleCombinations(state) || {};
    const runnerFailures = getSportsbookBettingImplyRunnerFailures(state);
    const runners = getSportsbookBettingRunners(state);
    const {
      totalPotentialReturns,
      totalOriginalPotentialReturns,
      totalCombinedStake,
      totalStake,
      combinations,
      legs,
      isBonusSelected,
      hasBonusMoney,
    } = getSportsbookBettingState(state);

    const combinationsValues = Object.values(combinations);
    const { loggedIn, isAuthenticating } = session;
    const isSummaryDisabled = !totalCombinedStake;
    const hasValidStakedCombinations = combinationsValues.some((combination) => {
      const validCombination = !!combination.totalCombinedStake;
      return validCombination && !hasLegsWithMarketFailures(combination, legs, runnerFailures);
    });
    const hasPriceBoostedCombination = combinationsValues.some(
      (combination) => combination.isPriceBoostSelected && !!combination.stake,
    );
    const isPlacing = getBetslipPlaceStatus(state) === "INPROGRESS";
    const totalOriginalReturns = buildTotalOriginalReturns(
      totalCombinedStake,
      totalPotentialReturns,
      totalOriginalPotentialReturns,
      hasPriceBoostedCombination,
      localization,
    );
    const validations = getValidations(state, {
      localization,
      maxPayoutTermsUrl: config.termsUrls.maxPayout,
      dailyPayoutLimit: capabilities.dailyPayoutLimit,
    });
    const isDepositRequired = getIsDepositRequired(state, balance);
    const requiredDepositValue = getSbkRequiredDepositValueSelector(state, balance);
    const castBetsCombinationIds = combinationsValues
      .filter((combination) => isCast(combination, legs))
      .map((combination) => combination.id);
    const hasCastBets = castBetsCombinationIds.length > 0;
    const multiBetBuilderIds = combinationsValues
      .filter((combination) => isMultiBetBuilder(combination))
      .map((combination) => combination.id);
    const hasMultiBetBuilder = multiBetBuilderIds.length > 0;
    const multiBetBuilderLegIds =
      multiBetBuilderIds.length > 0
        ? combinationsValues.find((combination) => isMultiBetBuilder(combination))?.legs ?? []
        : [];
    const multiBetBuilderGroups = getBettingEventGroupedLegs(state, multiBetBuilderLegIds, localization);
    const multiBetBuilderGroupsCount = Object.keys(multiBetBuilderGroups).length;
    const firstCombinationId = combinationsValues?.[0]?.id;
    const betBuilderIds = getBetBuilderCombinationIds(state);
    const unavailableBetBuilderCombinationGroups = getUnavailableBetBuilderCombinationGroups(
      combinations,
      runners,
      runnerFailures,
    );
    const invalidCombinationGroups = getFailedCombinationGroups(state);
    const unavailableBetBuilderCombinationGroupSet = new Set(unavailableBetBuilderCombinationGroups);
    const activeBetBuilderCombinationGroups = new Set(
      combinationsValues
        .filter(({ id }) => betBuilderIds.includes(id))
        .map(({ combinationGroup }) => combinationGroup)
        .filter((combinationGroup): combinationGroup is number => combinationGroup != null),
    );
    const failedCombinationGroups = invalidCombinationGroups.filter(
      (combinationGroup) =>
        !unavailableBetBuilderCombinationGroupSet.has(combinationGroup) &&
        !activeBetBuilderCombinationGroups.has(combinationGroup),
    );
    const failedCombinationGroupIds = getBoostedUncombinedGroupIds(state);
    const uniqueRunnersFailures = getAllUniqueRunnersFailures(state);
    const hasBetBuilders = !!(betBuilderIds.length || failedCombinationGroups.length);
    const hasMultipleLinesMultiples = !!multiLinesCombinations?.length;
    const hasOneLineMultiple = !!oneLineCombination?.id;
    const betBuilderLegCounts = Object.values(combinations)
      .filter((combination) => isBetBuilder(combination) || isMultiBetBuilder(combination))
      .map((combination) => ({
        id: combination.id,
        legCount: isMultiBetBuilder(combination) ? multiBetBuilderLegIds.length : combination.legs.length,
      }));
    const oddsWithMovement = Object.values(oddsMovementMap).filter((each) => each.movement !== null);
    const hasAnyOddChanged = oddsWithMovement.length >= 1;
    const transactionalError = buildSportsbookTransactionalError(state);
    const isBetConfirmationStepEnabled = capabilities.betConfirmationStep.enabled;
    const boostedCombinations = getBoostedCombinations(state);
    const boostedCombinationIds = boostedCombinations.map((combination) => combination.id);
    const hasPriceBoost = boostedCombinationIds.length > 0 || failedCombinationGroupIds.length > 0;
    const lotteriesCombinationIds = combinationsValues
      .filter((combination) => isLotteries(combination, legs))
      .map((combination) => combination.id);
    const hasLotteries = lotteriesCombinationIds.length > 0;
    const singlesCombinationIds = getSingleCombinationIds(state);
    const nonLotterySingleIds = singlesCombinationIds.filter((id) => !lotteriesCombinationIds.includes(id));
    const hasSingles = nonLotterySingleIds.length > 0;
    const hasOnlyOneSingle = !getHasMultiples(state) && singlesCombinationIds.length === 1;
    const quickBetslipBet = getQuickBetslipBet(state);
    const isQuickBetslipActive = quickBetslipBet?.status === "valid";
    const castGroupIds = getCastGroupIds(state);
    const { oddsDisplay: sportsbookOddsDisplay, acceptOddsMovement: oddsMovement } = preferences;
    const isUpsellSuggestionsEnabled = capabilities.upsellSuggestions.enabled;
    const isSinglesFirst = getSinglesOnTopSelector(state);
    const withAllPebbleEnabled = capabilities.withAllPebble.enabled;
    const { isDesktopLayout } = config;

    const isOddsMovementTransactionalError = uniqueRunnersFailures.includes(
      RUNNER_FAILURE_CODES.REQUESTED_PRICE_NOT_AVAILABLE,
    );
    const isAcceptOddsMovementAlertEnabled = capabilities.acceptOddsMovementAlert.enabled;
    const hasUserChangedOddsMovement = getHasUserChangedOddsMovementPreference(state);

    const hasBlockingValidations = validations.some(
      (validation) =>
        validation.type !== VALIDATION_TYPES.BONUS_USAGE_ACCA_INSURANCE_INCOMPATIBLE &&
        validation.notification?.type === AlertType.Error,
    );
    const isPlaceDisabled = !hasValidStakedCombinations || hasBlockingValidations;

    const formattedTotalCombinedStake = currencyFormatWithDecimalPlaces({
      ...localization,
      value: totalCombinedStake,
      decimalPlaces: 2,
    });

    const shouldDisplayPlaceholder = !legs || !Object.keys(legs).length;

    let freeBetsAlertMessage: string | undefined;

    if (hasGenerosityWallets) {
      const allSelectedWalletsAmounts = Object.values(
        getCombinationsSelectedWalletsAmounts(state.betting.sportsbookBetting),
      );
      const combination = hasOnlyOneSingle ? allSelectedWalletsAmounts?.[0] : undefined;
      const totalWalletsAmounts = allSelectedWalletsAmounts.reduce(
        (accAmount, { combinationAmount }) => sumWalletsAmounts(accAmount, combinationAmount),
        0,
      );

      freeBetsAlertMessage = buildFreeBetsAlertMessage({
        numLines: combination?.numLines,
        combinationAmountPerLine: combination?.combinationAmountPerLine,
        localization,
        combinationAmount: totalWalletsAmounts,
      });
    }

    const quickStakes = getQuickStakes(preferences.quickStakes, localization);
    const allRunnerIds = Object.keys(runners);
    const failedRunnerIds = Object.keys(runnerFailures);
    const isEveryRunnerFailing =
      allRunnerIds.length > 0 &&
      allRunnerIds.length === failedRunnerIds.length &&
      allRunnerIds.every((id) => failedRunnerIds.includes(id));
    const allRunnerFailures = Object.values(runnerFailures);
    const isClosed =
      isEveryRunnerFailing &&
      allRunnerFailures.every((failures) => !!failures?.length && hasAllMarketClosedFailure(failures));
    const isSuspended =
      isEveryRunnerFailing &&
      allRunnerFailures.every((failures) => !!failures?.length && hasAllMarketSuspendedFailure(failures));
    const [firstMovement] = oddsWithMovement;
    const latestOddsMovement =
      firstMovement && hasOnlyOneSingle && combinations[firstMovement.id]
        ? buildCombinationOdds(combinations[firstMovement.id], sportsbookOddsDisplay)
        : undefined;
    const totalReturns = isClosed
      ? i18nLabels.betslipNotAvailable
      : buildPotentialReturns(totalCombinedStake, totalPotentialReturns, localization);

    const { label, secondaryLabel, loadingLabel, reverseLabels } = buildSbkPlaceBetButtonLabels({
      isPlacing,
      isAuthenticating,
      isDepositRequired,
      isLoggedIn: loggedIn,
      hasStake: !!totalCombinedStake,
      hasOddsChanged: hasAnyOddChanged,
      hasGenerosityWallets,
      hasGenerosityTokens,
      isClosed,
      isSuspended,
      shouldAcceptOddsMovement: oddsMovement,
      interpolatedValues: {
        odds: latestOddsMovement,
        stake: formattedTotalCombinedStake,
      },
    });

    const accountBalance = balance.available ?? 0;

    const balanceAfterBet = isClosed
      ? i18nLabels.betslipNotAvailable
      : buildSbkBalanceAfterBetLabel(localization, {
          accountBalance,
          totalStake: totalStake ?? 0,
          isOldUseBonusActive: isBonusSelected,
          isLoggedIn: loggedIn,
        });

    const shouldFocusMultiple =
      (hasOneLineMultiple && !hasPriceBoost && !hasMultipleLinesMultiples && !isDesktopLayout) ||
      (hasPriceBoost &&
        boostedCombinationIds.length === 1 &&
        !hasOneLineMultiple &&
        !hasMultipleLinesMultiples &&
        !isDesktopLayout);

    const shouldFocusBetBuilder = hasMultiBetBuilder
      ? false
      : hasBetBuilders && betBuilderIds.length === 1 && !isDesktopLayout;

    const shouldFocusCastBet = hasCastBets && castGroupIds.length === 1 && !isDesktopLayout;

    const marketSelections: MarketSelection[] = Object.values(runners).map((runner) => ({
      selectionId: runner.selectionId,
      marketId: runner.marketId,
    }));

    const oddsMovementLabels = acceptOddsMovementLabels(oddsMovement, hasUserChangedOddsMovement);

    const buildSelectionsCountTitle = (numberOfSelections: number) =>
      i18n({
        key: "I18N.BETSLIP.SELECTIONS_COUNT",
        interpolationValues: { numberOfSelections: `${numberOfSelections}` },
      });

    const countValidLegs = (legIds: string[] = []) =>
      legIds.filter((legId) => {
        const bettingLeg = legs[legId];

        if (!bettingLeg) return true;

        return bettingLeg.runners.every((runnerId) => !runnerFailures[runnerId]?.length);
      }).length;

    const selectionsCountTitle: Record<string, string> = {};
    const betBuilderTitles: Record<string, string> = {};

    if (oneLineCombination) {
      selectionsCountTitle[oneLineCombination.id] = buildSelectionsCountTitle(countValidLegs(oneLineCombination.legs));
    }

    betBuilderLegCounts.forEach(({ id, legCount }) => {
      const selectionsCount = buildSelectionsCountTitle(legCount);
      selectionsCountTitle[id] = selectionsCount;
      betBuilderTitles[id] = `${i18nLabels.betBuilder} (${selectionsCount})`;
    });

    const eventCountTitle = i18n({
      key: "I18N.BETSLIP.EVENTS_COUNT",
      interpolationValues: { numberOfEvents: `${multiBetBuilderGroupsCount}` },
    });

    const multiBetBuilderTitle = hasMultiBetBuilder
      ? `${i18nLabels.multiBetBuilder} (${eventCountTitle} - ${selectionsCountTitle[multiBetBuilderIds[0]] ?? ""})`
      : "";

    const oneLineMultipleValidSelectionsCount = oneLineCombination ? countValidLegs(oneLineCombination.legs) : 0;

    const oneLineMultipleTitle = oneLineCombination
      ? oneLineMultipleValidSelectionsCount > 0
        ? `${i18nLabels.multiples} (${buildSelectionsCountTitle(oneLineMultipleValidSelectionsCount)})`
        : i18nLabels.multiples
      : "";

    return {
      marketSelections,
      failedCombinationGroups,
      failedCombinationGroupIds,
      betBuilderIds,
      multiBetBuilderIds,
      hasSingles,
      hasOnlyOneSingle,
      oneLineMultipleId: oneLineCombination?.id ?? "",
      isQuickBetslipActive,
      hasPriceBoost,
      boostedCombinationIds,
      totalReturns,
      totalOriginalReturns,
      isPlaceDisabled,
      isPanelDisabled: isPlacing || !!isAuthenticating,
      isSummaryDisabled,
      hasOneLineMultiple,
      hasMultipleLinesMultiples,
      hasMultiBetBuilder,
      hasCastBets,
      hasBetBuilders,
      hasLotteries,
      shouldFocusMultiple,
      shouldFocusBetBuilder,
      shouldFocusCastBet,
      hasPlaceError: !!transactionalError,
      firstCombinationId,
      isFreeBetsSelected: !!isBonusSelected && !hasGenerosityWallets,
      isEligibleToBonus: !!hasBonusMoney && !hasGenerosityWallets,
      isLoggedIn: loggedIn,
      isDepositRequired,
      requiredDepositValue,
      isCollapsed,
      isOddsMovementOn: oddsMovement,
      isSinglesFirst,
      withAllPebbleEnabled,
      showAcceptOddsMovementAlert: isAcceptOddsMovementAlertEnabled ? isOddsMovementTransactionalError : false,
      oddsMovementLabels,
      i18n: i18nLabels,
      placeBtnLabel: label,
      placeBtnSecondaryLabel: secondaryLabel,
      placeBtnLoadingLabel: loadingLabel,
      reversePlaceBtnLabels: reverseLabels,
      isOddsBoosted: hasPriceBoostedCombination,
      termsUrl,
      shouldDisplayPlaceholder,
      isBetConfirmationStepEnabled,
      freeBetsAlertMessage,
      balanceAfterBet,
      quickStakes,
      multiBetBuilderTitle,
      oneLineMultipleTitle,
      betBuilderTitles,
      currencySymbol,
      separator: toKeyboardSeparator(localization.decimalSeparator),
      isUpsellSuggestionsEnabled,
    };
  };
};

export type SelectionRemovePayload = {
  legId: string;
  runnerUrn: string;
};

export type DispatchProps = {
  dispatchSelectionRemove: (payload: SelectionRemovePayload) => void;
  dispatchPlacement: () => void;
  dispatchRemoveAll: () => void;
  dispatchSportsbookBonusToggle: (isFreeBetsSelected: boolean) => void;
  dispatchTabSwitch: (tabName: string) => void;
  requestLogin: () => void;
  dispatchConfirmBet: () => void;
  dispatchDepositRedirect: (amount?: number) => void;
  dispatchLoginToPlaceBetAction: () => void;
  dispatchFreeBetsRemoveAction: () => void;
  dispatchIncrementPress: (combinationId: string, quickStakeValue: number, currencySymbol?: string) => void;
  dispatchOddsMovementChange: SwitchOnChange;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchSelectionRemove: ({ legId, runnerUrn }: SelectionRemovePayload) => {
    dispatch<BetslipSportsbookRemoveLegClick>({
      type: UI__BETSLIP_SBK_REMOVE_LEG_CLICK,
      payload: { legId, runnerUrn },
    });
    dispatch<BettingSportsbookRemoveLegAction>({
      type: BETTING__SBK_REMOVE_LEG_ACTION,
      payload: { legId },
    });
  },
  dispatchPlacement: () => {
    dispatch<BetslipSportsbookPlaceBetsClick>({
      type: UI__BETSLIP_SBK_PLACE_BETS_CLICK,
    });
    dispatch<BettingSportsbookPlaceBetsAction>({
      type: BETTING__SBK_PLACE_BETS,
    });
  },
  dispatchRemoveAll: (): void => {
    dispatch<BetslipSportsbookRemoveSelectionsClick>({
      type: UI__BETSLIP_SBK_REMOVE_SELECTIONS,
    });

    dispatch<BettingSportsbookClearAction>({
      type: BETTING__SBK_CLEAR_ACTION,
    });
  },
  dispatchTabSwitch: (tabName: string) => {
    dispatch<BetslipSportsbookTabSwitchAction>({
      type: UI__BETSLIP_SBK_TAB_SWITCH,
      payload: { tabName },
    });
  },
  dispatchSportsbookBonusToggle: (isFreeBetsSelected: boolean) => {
    dispatch<BettingSportsbookBonusToggleAction>({
      type: BETTING__SBK_BONUS_TOGGLE_ACTION,
      payload: {
        isFreeBetsSelected: !isFreeBetsSelected,
        product: Product.Sportsbook,
      },
    });
  },
  requestLogin: () => {
    emitBetslipLoginRequested();
  },
  dispatchConfirmBet: () => {
    dispatch<BetslipSportsbookPlaceBetsClick>({
      type: UI__BETSLIP_SBK_PLACE_BETS_CLICK,
    });
    dispatch<BettingSportsbookConfirmBetsAction>({
      type: BETTING__SBK_CONFIRM_BETS,
    });
  },
  dispatchDepositRedirect: (amount?: number) => {
    dispatch<BetslipSportsbookDepositToPlaceBetClick>({ type: UI__BETSLIP_SBK_DEPOSIT_TO_PLACE_BET_CLICK });
    dispatch<BettingDepositToPlaceBetAction>({ type: BETTING__DEPOSIT_TO_PLACE_BET });
    emitBetslipDepositToPlaceRequested({ amount });
  },
  dispatchLoginToPlaceBetAction: () => {
    dispatch<BetslipSportsbookLoginToPlaceBetClickAction>({
      type: UI__BETSLIP_SBK_LOGIN_TO_PLACE_BET_CLICK,
    });
  },
  dispatchFreeBetsRemoveAction: () => {
    dispatch<BettingSportsbookRemoveAllWalletsAction>({
      type: BETTING__SPORTSBOOK_REMOVE_ALL_FREE_BETS_WALLETS_ACTION,
    });
  },
  dispatchIncrementPress: (combinationId, quickStakeValue, currencySymbol) => {
    dispatch<BettingSportsbookIncrementStakeAction>({
      type: BETTING__SBK_INCREMENT_STAKE_ACTION,
      payload: { combinationId, increment: quickStakeValue },
    });
    dispatch<BetslipSportsbookIncrementStakeAction>({
      type: UI__BETSLIP_SBK_INCREMENT_STAKE_ACTION,
      payload: { increment: quickStakeValue, currencySymbol },
    });
  },
  dispatchOddsMovementChange: (isChecked: boolean) => {
    dispatch<BetslipSportsbookAcceptOddsMovementToggleAction>({
      type: UI__BETSLIP_SBK_ACCEPT_ODDS_MOVEMENT_TOGGLE,
      payload: { enabled: isChecked },
    });
    emitAcceptOddsMovementPreferenceToggled({ enabled: isChecked });
  },
});
